import { useState } from 'react';
import { useAuth, UserType } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

type Step = 'select-type' | 'form';

export default function RegisterModal({ isOpen, onClose, onSwitchToLogin }: RegisterModalProps) {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('select-type');
  const [userType, setUserType] = useState<UserType | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nombreEmpresa: '',
    sector: '',
    tamanoEmpresa: '',
    nombre: '',
    experiencia: '',
    pais: '',
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSelectType = (type: UserType) => {
    setUserType(type);
    setStep('form');
  };

  const handleBack = () => {
    setStep('select-type');
    setUserType(null);
    setError('');
    setPrivacyAccepted(false);
  };

  const handleClose = () => {
    // Resetear completamente el modal al cerrarse
    setStep('select-type');
    setUserType(null);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      nombreEmpresa: '',
      sector: '',
      tamanoEmpresa: '',
      nombre: '',
      experiencia: '',
      pais: '',
    });
    setPrivacyAccepted(false);
    setError('');
    setLoading(false);
    setSuccess(false);
    onClose();
  };

  const getSpecificError = (err: unknown): string => {
    if (err instanceof Error) {
      const msg = err.message.toLowerCase();
      
      // Errores específicos de Supabase
      if (msg.includes('user already registered') || msg.includes('already registered')) {
        return 'Este email ya está registrado. Prueba a iniciar sesión.';
      }
      if (msg.includes('password should be at least 6 characters')) {
        return 'La contraseña debe tener al menos 6 caracteres.';
      }
      if (msg.includes('invalid email') || msg.includes('email is invalid')) {
        return 'El formato del email no es válido.';
      }
      if (msg.includes('network') || msg.includes('fetch')) {
        return 'Error de conexión. Verifica tu internet e inténtalo de nuevo.';
      }
      
      return err.message;
    }
    return 'Error al crear la cuenta. Por favor, inténtalo de nuevo.';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validaciones previas
    if (!privacyAccepted) {
      setError('Debes aceptar la Política de Privacidad para continuar.');
      return;
    }

    if (userType === 'candidato' && !formData.nombre.trim()) {
      setError('El nombre completo es obligatorio.');
      return;
    }

    if (userType === 'empresa' && !formData.nombreEmpresa.trim()) {
      setError('El nombre de la empresa es obligatorio.');
      return;
    }

    if (!formData.email.trim()) {
      setError('El email es obligatorio.');
      return;
    }

    // Validación de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('El formato del email no es válido.');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);

    try {
      // Llamar a Supabase Auth con user_metadata
      const result = await register({
        email: formData.email,
        password: formData.password,
        userType: userType!,
        ...(userType === 'empresa'
          ? {
              nombreEmpresa: formData.nombreEmpresa,
              sector: formData.sector,
              tamanoEmpresa: formData.tamanoEmpresa,
            }
          : {
              nombre: formData.nombre,
              sector: formData.sector,
              experiencia: formData.experiencia,
              pais: formData.pais,
            }),
      });

      // Verificar si Supabase requiere confirmación de email
      if (result?.user && !result.session) {
        // Email no confirmado: mostrar mensaje claro
        setError('Te hemos enviado un email de confirmación. Por favor, verifica tu bandeja de entrada antes de iniciar sesión.');
        setLoading(false);
        return;
      }

      // Registro exitoso con sesión activa
      setSuccess(true);
      
      // Redirigir después de un breve delay
      setTimeout(() => {
        handleClose();
        if (userType === 'empresa') {
          navigate('/empresa');
        } else {
          navigate('/perfil');
        }
      }, 1500);
    } catch (err) {
      console.error('[Registro] Error:', err);
      setError(getSpecificError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {step === 'select-type' ? (
          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Crear cuenta</h2>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <p className="text-gray-600 mb-8">Selecciona el tipo de cuenta que deseas crear</p>

            <div className="space-y-4">
              <button
                onClick={() => handleSelectType('empresa')}
                className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-[#1A2B4A] hover:bg-gray-50 transition-all group text-left cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center group-hover:bg-[#1A2B4A] transition-colors">
                    <i className="ri-building-line text-2xl text-[#1A2B4A] group-hover:text-white transition-colors"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Soy una empresa</h3>
                    <p className="text-sm text-gray-600">Busco talento cualificado para procesos de selección internacional</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleSelectType('candidato')}
                className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-[#1A2B4A] hover:bg-gray-50 transition-all group text-left cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center group-hover:bg-[#1A2B4A] transition-colors">
                    <i className="ri-user-line text-2xl text-[#1A2B4A] group-hover:text-white transition-colors"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Soy un profesional</h3>
                    <p className="text-sm text-gray-600">Busco oportunidades laborales en Europa</p>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600">
                ¿Ya tienes cuenta?{' '}
                <button onClick={onSwitchToLogin} className="text-[#1A2B4A] font-medium hover:underline cursor-pointer">
                  Inicia sesión
                </button>
              </p>
            </div>
          </div>
        ) : (
          <div className="p-8">
            <div className="flex items-center gap-4 mb-6">
              <button onClick={handleBack} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line text-2xl"></i>
              </button>
              <h2 className="text-2xl font-bold text-gray-900">
                {userType === 'empresa' ? 'Registro de empresa' : 'Registro de profesional'}
              </h2>
              <button onClick={handleClose} className="ml-auto text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-green-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-green-700 text-sm font-medium leading-relaxed">
                  Cuenta creada. Redirigiendo...
                </p>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-red-700 text-sm leading-relaxed">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              {userType === 'empresa' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la empresa <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      required
                      value={formData.nombreEmpresa}
                      onChange={(e) => setFormData({ ...formData, nombreEmpresa: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                      placeholder="Nombre de tu empresa"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sector</label>
                    <select
                      value={formData.sector}
                      onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Selecciona un sector</option>
                      <option value="Tecnología">Tecnología</option>
                      <option value="Salud">Salud</option>
                      <option value="Ingeniería">Ingeniería</option>
                      <option value="Finanzas">Finanzas</option>
                      <option value="Manufactura">Manufactura</option>
                      <option value="Servicios">Servicios</option>
                      <option value="Construcción">Construcción</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tamaño de la empresa</label>
                    <select
                      value={formData.tamanoEmpresa}
                      onChange={(e) => setFormData({ ...formData, tamanoEmpresa: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Selecciona el tamaño</option>
                      <option value="1-10">1-10 empleados</option>
                      <option value="11-50">11-50 empleados</option>
                      <option value="51-200">51-200 empleados</option>
                      <option value="201-500">201-500 empleados</option>
                      <option value="500+">Más de 500 empleados</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre completo <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sector profesional</label>
                    <select
                      value={formData.sector}
                      onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Selecciona tu sector</option>
                      <option value="Tecnología">Tecnología</option>
                      <option value="Salud">Salud</option>
                      <option value="Ingeniería">Ingeniería</option>
                      <option value="Finanzas">Finanzas</option>
                      <option value="Manufactura">Manufactura</option>
                      <option value="Servicios">Servicios</option>
                      <option value="Construcción">Construcción</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Años de experiencia</label>
                    <select
                      value={formData.experiencia}
                      onChange={(e) => setFormData({ ...formData, experiencia: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Selecciona tu experiencia</option>
                      <option value="0-2 años">0-2 años</option>
                      <option value="3-5 años">3-5 años</option>
                      <option value="6-10 años">6-10 años</option>
                      <option value="Más de 10 años">Más de 10 años</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">País de residencia</label>
                    <input
                      type="text"
                      value={formData.pais}
                      onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                      placeholder="Tu país actual"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar contraseña <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  placeholder="Repite tu contraseña"
                />
              </div>

              <div className="flex items-start gap-3 pt-1">
                <input
                  type="checkbox"
                  id="register-privacy-checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-[#1A2B4A] border-gray-300 rounded focus:ring-[#1A2B4A] cursor-pointer flex-shrink-0"
                />
                <label htmlFor="register-privacy-checkbox" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                  He leído y acepto la{' '}
                  <a href="/politica-privacidad" target="_blank" rel="noopener noreferrer" className="text-[#1A2B4A] font-medium hover:underline">
                    Política de Privacidad
                  </a>.
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !privacyAccepted || success}
                className="w-full py-3 bg-[#1A2B4A] text-white font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Creando cuenta...
                  </span>
                ) : success ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    ¡Cuenta creada!
                  </span>
                ) : (
                  'Crear cuenta'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                ¿Ya tienes cuenta?{' '}
                <button onClick={onSwitchToLogin} className="text-[#1A2B4A] font-medium hover:underline cursor-pointer">
                  Inicia sesión
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}